<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
              >
                Increment Report Search
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <!-- Filters Row 1 -->
                <v-row class="py-1" style="margin-bottom: -22px;">
                  <v-col md="3" cols="12">
                    <v-select
                      v-model="filters.department_id"
                      :items="departments"
                      item-title="name"
                      item-value="id"
                      label="Department"
                      variant="outlined"
                      density="compact"
                      bg-color="grey-lighten-4"
                      clearable
                      prepend-inner-icon="mdi-office-building"
                    ></v-select>
                  </v-col>
                  <v-col md="3" cols="12">
                    <v-select
                      v-model="filters.designation_id"
                      :items="designations"
                      item-title="name"
                      item-value="id"
                      label="Designation"
                      variant="outlined"
                      density="compact"
                      bg-color="grey-lighten-4"
                      clearable
                      prepend-inner-icon="mdi-badge-account"
                    ></v-select>
                  </v-col>
                  <v-col md="3" cols="12">
                    <v-select
                      v-model="filters.employee_type_id"
                      :items="employeeTypes"
                      item-title="name"
                      item-value="id"
                      label="Employee Type"
                      variant="outlined"
                      density="compact"
                      bg-color="grey-lighten-4"
                      clearable
                      prepend-inner-icon="mdi-account-tie"
                    ></v-select>
                  </v-col>
                  <v-col md="3" cols="12">
                    <!-- Empty space for alignment -->
                  </v-col>
                </v-row>

                <!-- Date Range Filters and Search Button Row 2 -->
                <v-row class="py-0" style="margin-top: -8px;">
                  <v-col md="3" cols="12">
                    <v-date-input
                      input-format="dd/mm/yyyy"
                      v-model="filters.from_date"
                      label="From Date"
                      density="compact"
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar-start"
                      variant="outlined"
                      bg-color="grey-lighten-4"
                      autocomplete="off"
                      clearable
                    ></v-date-input>
                  </v-col>
                  <v-col md="3" cols="12">
                    <v-date-input
                      input-format="dd/mm/yyyy"
                      v-model="filters.to_date"
                      label="To Date"
                      density="compact"
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar-end"
                      variant="outlined"
                      bg-color="grey-lighten-4"
                      autocomplete="off"
                      clearable
                    ></v-date-input>
                  </v-col>
                  <v-col md="3" cols="12">
                    <div class="d-flex justify-start align-center" style="gap: 12px;">
                      <v-btn 
                        class="bg-brand w-15" 
                        type="submit" 
                        ref="searchButton"
                        @click="loadReport"
                      >
                        Search
                      </v-btn>
                      <v-btn
                        class="bg-warning w-15"
                        type="button"
                        prepend-icon="mdi-refresh"
                        @click="resetFilters"
                        title="Reset all filters and search"
                      >
                        Reset
                      </v-btn>
                    </div>
                  </v-col>
                  <v-col md="3" cols="12">
                    <!-- Empty space for alignment -->
                  </v-col>
                </v-row>

                <!-- Increment Report List Expansion Panel -->
                <v-expansion-panels v-model="panel">
                  <v-expansion-panel>
                    <v-expansion-panel-title
                      color="blue-grey-lighten-4"
                      class="panel-header"
                    >
                      Increment Report List
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                      <!-- Search Form and Export Buttons Row -->
                      <v-row class="py-1">
                        <v-col md="3" cols="12">
                          <v-text-field
                            v-model="search"
                            append-inner-icon="mdi-magnify"
                            label="Search"
                            single-line
                            hide-details
                            density="compact"
                            variant="outlined"
                            bg-color="grey-lighten-4"
                            clearable
                          ></v-text-field>
                        </v-col>
                        <v-col md="6" cols="12">
                          <div class="d-flex justify-center">
                            <v-btn-group
                              variant="outlined"
                              density="compact"
                              size="x-small"
                              divided
                            >
                              <v-btn
                                prepend-icon="mdi mdi-download"
                                variant="flat"
                                color="success"
                                @click="exportToCSV"
                                :disabled="!filteredEmployees.length"
                              >CSV</v-btn>
                              <v-btn
                                prepend-icon="mdi mdi-download"
                                variant="flat"
                                color="primary"
                                @click="exportToExcel"
                                :disabled="!filteredEmployees.length"
                              >EXCEL</v-btn>
                              <v-btn
                                prepend-icon="mdi mdi-download"
                                variant="flat"
                                color="red"
                                @click="exportToPDF"
                                :disabled="!filteredEmployees.length"
                              >PDF</v-btn>
                            </v-btn-group>
                          </div>
                        </v-col>
                        <v-col md="3" cols="12">
                          <!-- Empty space for alignment -->
                        </v-col>
                      </v-row>

                      <!-- Data Table -->
                      <v-card class="overflow-x-auto">
                        <v-data-table
                          :headers="headers"
                          :items="filteredEmployees"
                          :loading="loading"
                          v-model:items-per-page="itemsPerPage"
                          density="compact"
                          class="min-w-[800px]"
                        >
                          <template v-slot:item.index="{ index }">
                            {{ ++index }}
                          </template>

                          <template v-slot:item.employee_name="{ item }">
                            <div class="d-flex align-center justify-center">
                              <v-avatar size="32" class="mr-3">
                                <v-img v-if="item.image_thumb" :src="getImageUrl(item.image_thumb)" />
                                <v-icon v-else size="32" color="grey">mdi-account</v-icon>
                              </v-avatar>
                              <div class="text-center">
                                <div class="font-weight-medium">{{ item.name }}</div>
                                <div class="text-caption text-grey">{{ item.name_bangla }}</div>
                              </div>
                            </div>
                          </template>

                          <template v-slot:item.employee_code="{ item }">
                            <div class="text-center">
                              <span class="font-weight-medium">{{ item.code }}</span>
                            </div>
                          </template>

                          <template v-slot:item.increment_date="{ item }">
                            <div class="d-flex justify-center">
                              <v-chip 
                                size="small" 
                                
                                variant="tonal"
                              >
                                {{ formattedDate(item.increment_date) }}
                              </v-chip>
                            </div>
                          </template>

                          <template v-slot:item.current_basic_salary="{ item }">
                            <div class="d-flex justify-center">
                              <v-chip 
                                size="small" 
                                
                                variant="tonal"
                              >
                                {{ formatCurrency(item.current_basic_salary) }}
                              </v-chip>
                            </div>
                          </template>

                          <template v-slot:item.new_basic_salary="{ item }">
                            <div class="d-flex justify-center">
                              <v-chip 
                                size="small" 
                                
                                variant="tonal"
                              >
                                {{ formatCurrency(item.new_basic_salary) }}
                              </v-chip>
                            </div>
                          </template>

                          <template v-slot:item.salary_effect_date="{ item }">
                            <div class="d-flex justify-center">
                              <v-chip 
                                size="small" 
                                
                                variant="tonal"
                              >
                                {{ formattedDate(item.salary_effect_date) }}
                              </v-chip>
                            </div>
                          </template>

                          <template v-slot:item.department="{ item }">
                            <div class="text-center">
                              <span v-if="item.department" class="text-body-2">{{ item.department.name }}</span>
                              <span v-else class="text-grey">-</span>
                            </div>
                          </template>

                          <template v-slot:item.designation="{ item }">
                            <div class="text-center">
                              <span v-if="item.designation" class="text-body-2">{{ item.designation.name }}</span>
                              <span v-else class="text-grey">-</span>
                            </div>
                          </template>

                          <template v-slot:item.employee_type="{ item }">
                            <div class="text-center">
                              <span v-if="item.employee_type" class="text-body-2">{{ item.employee_type.name }}</span>
                              <span v-else class="text-grey">-</span>
                            </div>
                          </template>

                          <template v-slot:item.mobile="{ item }">
                            <div class="d-flex justify-center">
                              <div v-if="item.mobile" class="d-flex align-center">
                                <v-icon size="12" class="mr-1" color="success">mdi-phone</v-icon>
                                <span>{{ item.mobile }}</span>
                              </div>
                              <span v-else class="text-grey">-</span>
                            </div>
                          </template>

                          <template v-slot:item.email="{ item }">
                            <div class="d-flex justify-center">
                              <div v-if="item.email" class="d-flex align-center">
                                <v-icon size="12" class="mr-1" color="info">mdi-email</v-icon>
                                <span class="text-truncate" style="max-width: 150px;">{{ item.email }}</span>
                              </div>
                              <span v-else class="text-grey">-</span>
                            </div>
                          </template>

                          <template v-slot:item.actions="{ item }">
                            <v-btn
                              @click="viewEmployee(item.id)"
                              icon="mdi-eye"
                              size="small"
                              color="primary"
                              variant="text"
                              title="View Employee"
                            ></v-btn>
                          </template>
                        </v-data-table>
                      </v-card>
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>

      <!-- No Data Message -->
      <v-row v-if="!loading && (!reportData.increment_months || reportData.increment_months.length === 0)">
        <v-col cols="12">
          <v-card>
            <v-card-text class="text-center py-12">
              <v-icon size="80" color="grey-lighten-1">mdi-account-search</v-icon>
              <div class="text-h5 mt-4 mb-2">No employees found</div>
              <div class="text-body-1 text-grey mb-4">Try adjusting your filters or add some employees first.</div>
              <v-btn color="primary" variant="elevated" @click="loadReport">
                <v-icon left>mdi-refresh</v-icon>
                Refresh Data
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script setup>
import { ref, onMounted, computed, getCurrentInstance, watch } from 'vue'
import { useRouter } from 'vue-router'
import Axios from 'axios'
import { prefix } from '@/store/api'
import formattedDate from '@/plugins/dateFormatter'

const router = useRouter()

// Get Vue instance for export functions
const instance = getCurrentInstance()

// Reactive data
const panel = ref([0])
const search = ref('')
const loading = ref(false)
const itemsPerPage = ref(10)
const reportData = ref({})
const departments = ref([])
const designations = ref([])
const employeeTypes = ref([])



// Filters
const filters = ref({
  department_id: null,
  designation_id: null,
  employee_type_id: null,
  from_date: null,
  to_date: null
})

// Table headers
const headers = ref([
  { title: '#SL', key: 'index', sortable: false, align: 'center' },
  { title: 'Employee Name', key: 'employee_name', sortable: true, align: 'center' },
  { title: 'Code', key: 'employee_code', sortable: true, align: 'center' },
  { title: 'Increment Date', key: 'increment_date', sortable: true, align: 'center' },
  { title: 'Before Increment', key: 'current_basic_salary', sortable: true, align: 'center' },
  { title: 'Current Basic Salary', key: 'new_basic_salary', sortable: true, align: 'center' },
  { title: 'Salary Effect Date', key: 'salary_effect_date', sortable: true, align: 'center' },
  { title: 'Designation', key: 'designation', sortable: true, align: 'center' },
  { title: 'Actions', key: 'actions', sortable: false, align: 'center', width: '80px' }
])

// Computed property to flatten employees from all increment months
const flattenedEmployees = computed(() => {
  if (!reportData.value.increment_months) return []
  
  const employees = []
  reportData.value.increment_months.forEach(month => {
    if (month.employees && month.employees.length > 0) {
      month.employees.forEach(employee => {
        employees.push({
          ...employee,
          increment_month: month.increment_month,
          month_name: month.month_name,
          // Add searchable fields for better search functionality
          employee_name: employee.name,
          employee_code: employee.code,
          designation_name: employee.designation?.name || '',
          department_name: employee.department?.name || ''
        })
      })
    }
  })
  
  return employees
})

// Computed property for filtered employees based on search
const filteredEmployees = computed(() => {
  if (!search.value) return flattenedEmployees.value
  
  const searchQuery = search.value.toLowerCase().trim()
  if (!searchQuery) return flattenedEmployees.value
  
  const filtered = flattenedEmployees.value.filter(employee => {
    // Search in employee name
    if (employee.name && employee.name.toLowerCase().includes(searchQuery)) return true
    
    // Search in employee code
    if (employee.code && employee.code.toLowerCase().includes(searchQuery)) return true
    
    // Search in designation
    if (employee.designation?.name && employee.designation.name.toLowerCase().includes(searchQuery)) return true
    
    // Search in department
    if (employee.department?.name && employee.department.name.toLowerCase().includes(searchQuery)) return true
    
    // Search in increment date
    if (employee.increment_date && employee.increment_date.toLowerCase().includes(searchQuery)) return true
    
    // Search in increment ref no
    if (employee.increment_ref_no && employee.increment_ref_no.toLowerCase().includes(searchQuery)) return true
    
    return false
  })
  
  return filtered
})

// Methods
const loadReport = async () => {
  loading.value = true
  try {
    const params = new URLSearchParams()
    if (filters.value.department_id) params.append('department_id', filters.value.department_id)
    if (filters.value.designation_id) params.append('designation_id', filters.value.designation_id)
    if (filters.value.employee_type_id) params.append('employee_type_id', filters.value.employee_type_id)
    if (filters.value.from_date) params.append('from_date', filters.value.from_date)
    if (filters.value.to_date) params.append('to_date', filters.value.to_date)

    const response = await Axios.get(`${prefix}hrm/increment-report?${params.toString()}`)
    
    if (response.data.success) {
      reportData.value = response.data.data
    }
  } catch (error) {
    console.error('Error loading increment report:', error)
  } finally {
    loading.value = false
  }
}

const loadFilters = async () => {
  try {
    // Load departments
    const deptResponse = await Axios.get(`${prefix}hrm/get-department-list`)
    if (deptResponse.data.success) {
      departments.value = deptResponse.data.data
    }

    // Load designations
    const desigResponse = await Axios.get(`${prefix}hrm/get-designation-list`)
    if (desigResponse.data.success) {
      designations.value = desigResponse.data.data
    }

    // Load employee types
    const etResponse = await Axios.get(`${prefix}hrm/get-employee-type-list`)
    if (etResponse.data.success) {
      employeeTypes.value = etResponse.data.data
    }
  } catch (error) {
    console.error('Error loading filter data:', error)
  }
}

const viewEmployee = (employeeId) => {
  router.push(`/hrm/employee-details/${employeeId}`)
}

const resetFilters = () => {
  // Reset all filters
  filters.value = {
    department_id: null,
    designation_id: null,
    employee_type_id: null,
    from_date: null,
    to_date: null
  }
  
  // Reset search
  search.value = ''
  
  // Reload report with cleared filters
  loadReport()
}

const exportToCSV = () => {
  const data = [['#', 'Employee Name', 'Code', 'Increment Date', 'Before Increment', 'Current Basic Salary', 'Salary Effect Date', 'Designation']]
  
  filteredEmployees.value.forEach((item, index) => {
    data.push([
      index + 1,
      item.name,
      item.code,
      formattedDate(item.increment_date),
      formatCurrency(item.current_basic_salary),
      formatCurrency(item.new_basic_salary),
      formattedDate(item.salary_effect_date),
      item.designation?.name || '-'
    ])
  })
  
  // Use Vue instance export functions
  instance.proxy.$exportTableToCSV(data, "increment_report_" + instance.proxy.$fileDate())
}

const exportToExcel = () => {
  const data = [['#', 'Employee Name', 'Code', 'Increment Date', 'Before Increment', 'Current Basic Salary', 'Salary Effect Date', 'Designation']]
  
  filteredEmployees.value.forEach((item, index) => {
    data.push([
      index + 1,
      item.name,
      item.code,
      formattedDate(item.increment_date),
      formatCurrency(item.current_basic_salary),
      formatCurrency(item.new_basic_salary),
      formattedDate(item.salary_effect_date),
      item.designation?.name || '-'
    ])
  })
  
  // Use Vue instance export functions
  instance.proxy.$exportTableToExcel(data, "increment_report_" + instance.proxy.$fileDate())
}

const exportToPDF = () => {
  let serialNumber = 1
  const columns = [
    "#", 
    "Employee Name", 
    "Code", 
    "Increment Date",
    "Before Increment",
    "Current Basic Salary",
    "Salary Effect Date",
    "Designation"
  ]
  const rows = filteredEmployees.value.map((item) => [
    serialNumber++,
    item.name,
    item.code,
    formattedDate(item.increment_date),
    formatCurrency(item.current_basic_salary),
    formatCurrency(item.new_basic_salary),
    formattedDate(item.salary_effect_date),
    item.designation?.name || '-'
  ])
  
  // Use Vue instance export functions
  instance.proxy.$exportTableToPDF(columns, rows, "Increment Report", "increment_report_" + instance.proxy.$fileDate())
}

const getImageUrl = (imagePath) => {
  if (!imagePath) return ''
  return `${prefix.replace('/api/', '')}${imagePath}`
}

const formatCurrency = (amount) => {
  if (!amount || amount === 0) return '0.00'
  try {
    return parseFloat(amount).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  } catch (error) {
    return '0.00'
  }
}

// Initialize
onMounted(() => {
  loadFilters()
  loadReport()
})
</script>

<style scoped>
.panel-header {
  font-weight: 600;
}

.min-w-\[1200px\] {
  min-width: 1200px;
}

/* Custom scrollbar for data table */
.v-data-table ::-webkit-scrollbar {
  height: 8px;
}

.v-data-table ::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.v-data-table ::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.v-data-table ::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
